import React, { useState, useContext, useEffect } from "react";
import {
  Card,
  Table,
  Timeline,
  Tag,
  Statistic,
  Row,
  Col,
  DatePicker,
  Select,
  Progress,
  Button,
  message,
  Spin,
  Modal,
  Space,
  Tooltip,
} from "antd";
import { Bar } from "react-chartjs-2";
import {
  AuditOutlined,
  ClockCircleOutlined,
  LineChartOutlined,
  FileDoneOutlined,
  FileExcelOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  BookOutlined,
} from "@ant-design/icons";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import moment from "moment";

// Custom CSS for tour highlight animation
const tourStyles = `
  @keyframes pulse-border {
    0% {
      box-shadow: 0 0 0 0 rgba(24, 144, 255, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(24, 144, 255, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(24, 144, 255, 0);
    }
  }
  .tour-highlight {
    animation: pulse-border 1.5s infinite;
    border-radius: 4px;
  }
`;

const { RangePicker } = DatePicker;

// Application stages mapping
const applicationStages = {
  1: { label: "Submitted", color: "blue" },
  2: { label: "Under Review", color: "gold" },
  3: { label: "Approved", color: "green" },
  7: { label: "Disbursed", color: "cyan" }, // Status 7 is for active loans (disbursed)
  8: { label: "Overdue", color: "orange" }, // Status 8 is for overdue loans
  9: { label: "Defaulted", color: "red" }, // Status 9 is for defaulted loans
  10: { label: "Rejected", color: "red" }, // Status 10 if application rejected
  12: { label: "App Application", color: "purple" }, // Submitted via the mobile app
};

const processLoanApplications = (data) => {
  // Define final statuses
  const finalStatuses = [7, 8, 9, 10]; // Disbursed, Overdue, Defaulted, Rejected

  return data.map((app) => {
    // Extract borrower info safely, handling potential missing data
    const borrowerFirstName = app.borrower?.first_name || "";
    const borrowerLastName = app.borrower?.last_name || "";

    // Determine processing_time
    const isFinalStatus = finalStatuses.includes(app.loan_status);
    const endDate =
      isFinalStatus && app.status_date ? moment(app.status_date) : moment(); // Use current date for non-final statuses

    const processing_time = endDate.diff(moment(app.date_created), "days");

    // Create application history from status changes
    let statusHistory = [];

    // Always add the creation date as the first "Submitted" entry
    statusHistory.push({
      status: 1, // Submitted
      timestamp: app.date_created,
    });

    // If the loan status is different from "Submitted", add it as a transition
    if (app.loan_status && app.loan_status !== 1) {
      // For approval date
      if (app.loan_status >= 3 && app.approval_date) {
        statusHistory.push({
          status: 3, // Approved
          timestamp: app.approval_date,
        });
      }

      // For disbursement (active) status
      if (app.loan_status === 7 && app.status_date) {
        statusHistory.push({
          status: 7, // Disbursed
          timestamp: app.status_date,
        });
      }

      // For rejection
      if (app.loan_status === 10 && app.status_date) {
        statusHistory.push({
          status: 10, // Rejected
          timestamp: app.status_date,
        });
      }
    }

    // Sort history by timestamp
    statusHistory.sort((a, b) => moment(a.timestamp).diff(moment(b.timestamp)));

    const lastStatusDate =
      statusHistory.length > 0
        ? moment(statusHistory[statusHistory.length - 1].timestamp)
        : moment(app.date_created);

    return {
      id: app.id,
      applicant: `${borrowerFirstName} ${borrowerLastName}`,
      applicantDetails: {
        nrc: app.borrower?.nrc || "N/A",
        phone: app.borrower?.phone || "N/A",
        address: app.borrower?.address || "N/A",
        employer: app.borrower?.employer || "N/A",
      },
      product: app.loan_product?.loan_name || "N/A",
      amount: parseFloat(app.amount) || 0,
      loan_purpose: app.loan_purpose || "N/A",
      application_date: moment(app.date_created),
      current_stage: applicationStages[app.loan_status] || {
        label: "Unknown",
        color: "gray",
      },
      status_history: statusHistory,
      //processing_time: moment().diff(moment(app.date_created), "days"),
      processing_time: lastStatusDate.diff(moment(app.date_created), "days"),
      assigned_officer: app.assigned_to?.first_name
        ? `${app.assigned_to.first_name} ${app.assigned_to.last_name || ""}`
        : "Unassigned",
    };
  });
};

const ApplicationPipelineReport = () => {
  const { company } = useContext(AppContext);
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [loanProducts, setLoanProducts] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: [moment().subtract(2, "years"), moment()],
    status: "all",
    product: "all",
  });

  // Tour guide state
  const [showTourModal, setShowTourModal] = useState(false);
  const [currentTourStep, setCurrentTourStep] = useState(0);
  const [tourHighlight, setTourHighlight] = useState(null);

  // Tour guide content - similar to other reports
  const tourGuide = [
    {
      title: "Welcome to the Application Pipeline Report",
      content:
        "This report helps you track and analyze the flow of loan applications through your lending process, from submission to final decision.",
      highlight: "title-section",
    },
    {
      title: "Date Range Filter",
      content:
        "Select a specific period to analyze application flow. By default, the report shows the last 2 years.",
      highlight: "date-filter",
    },
    {
      title: "Status Filter",
      content:
        "Filter applications by their current stage - Submitted, Under Review, Approved, etc.",
      highlight: "status-filter",
    },
    {
      title: "Product Filter",
      content:
        "Filter applications by loan product to analyze performance of specific offerings.",
      highlight: "product-filter",
    },
    {
      title: "Key Metrics",
      content:
        "These cards show important metrics: total applications, conversion rate, average processing time, and applications under review.",
      highlight: "metrics-row",
    },
    {
      title: "Stage Distribution Chart",
      content:
        "This chart shows the distribution of applications across different stages in your pipeline.",
      highlight: "stage-chart",
    },
    {
      title: "Stage Progression Timeline",
      content:
        "See how applications move through each stage and the conversion rate at each step.",
      highlight: "stage-timeline",
    },
    {
      title: "Detailed Applications Table",
      content:
        "Review detailed information about each application including the processing time and complete status history.",
      highlight: "applications-table",
    },
    {
      title: "Export Feature",
      content:
        "Export the report data to CSV for further analysis or sharing with your team.",
      highlight: "export-button",
    },
  ];

  // Function to fetch loan applications from the API
  useEffect(() => {
    const fetchApplications = async () => {
      if (!company?.id || !token) return;

      try {
        setLoading(true);

        // Get date range for filtering
        const startDate = filters.dateRange[0].format("YYYY-MM-DD");
        const endDate = filters.dateRange[1].format("YYYY-MM-DD");

        // console.log("Date range filter:", startDate, "to", endDate);

        // Fetch all loans with their relationships
        const response = await axios.get(
          `https://zedlenders.pickmesms.com/items/loans?filter[company]=${company.id}&filter[date_created][_between]=${startDate},${endDate}&fields=*,borrower.first_name,borrower.last_name,borrower.nrc,borrower.phone,borrower.address,borrower.business,loan_product.loan_name,assigned_to.first_name,assigned_to.last_name`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // console.log("Loans API response:", response.data);

        // Fetch loan products for the filter dropdown
        const productsResponse = await axios.get(
          `https://zedlenders.pickmesms.com/items/loan_products?filter[company]=${company.id}&filter[is_active][_eq]=true`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Apply status filtering if needed
        let filteredData = response.data.data;
        if (filters.status !== "all") {
          filteredData = filteredData.filter(
            (loan) => loan.loan_status == filters.status
          );
        }

        // Apply product filtering if needed
        if (filters.product !== "all") {
          filteredData = filteredData.filter(
            (loan) => loan.loan_product?.id == filters.product
          );
        }

        // Process the filtered data
        const processedApplications = processLoanApplications(filteredData);
        setApplications(processedApplications);
        setLoanProducts(productsResponse.data.data || []);
      } catch (error) {
        // console.error("Failed to fetch application data:", error);
        message.error("Failed to load application pipeline data");
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [company?.id, token, filters]);

  const getStageMetrics = () => {
    const metrics = {
      total: applications.length,
      byStage: {},
      conversionRate: 0,
      avgProcessingTime: 0,
    };

    applications.forEach((app) => {
      metrics.byStage[app.current_stage.label] =
        (metrics.byStage[app.current_stage.label] || 0) + 1;
      metrics.avgProcessingTime += app.processing_time;
    });

    // Calculate conversion rate as (Disbursed + Active) / Total
    const disbursedCount = metrics.byStage["Disbursed"] || 0;
    metrics.conversionRate =
      applications.length > 0
        ? ((disbursedCount / applications.length) * 100).toFixed(1)
        : 0;

    metrics.avgProcessingTime =
      applications.length > 0
        ? (metrics.avgProcessingTime / applications.length).toFixed(1)
        : 0;

    return metrics;
  };

  const metrics = getStageMetrics();

  const chartData = {
    labels: Object.values(applicationStages).map((s) => s.label),
    datasets: [
      {
        label: "Applications by Stage",
        data: Object.values(applicationStages).map(
          (s) => metrics.byStage[s.label] || 0
        ),
        backgroundColor: Object.values(applicationStages).map((s) => s.color),
      },
    ],
  };

  const formatLoanPurpose = (purpose) => {
    const purposeMap = {
      business_investment: "Business Investment",
      working_capital: "Working Capital",
      equipment_purchase: "Equipment Purchase",
      inventory_purchase: "Inventory Purchase",
      home_improvement: "Home Improvement",
      education: "Education",
      medical_emergency: "Medical Emergency",
      wedding: "Wedding",
      vehicle_purchase: "Vehicle Purchase",
      debt_consolidation: "Debt Consolidation",
      agricultural_investment: "Agricultural Investment",
      other: "Other",
    };
    return purposeMap[purpose] || purpose || "N/A";
  };

  const columns = [
    {
      title: "Applicant",
      dataIndex: "applicant",
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: "bold" }}>{text}</div>
          <div style={{ fontSize: 11, color: "#666" }}>
            NRC: {record.applicantDetails.nrc}
          </div>
          <div style={{ fontSize: 11, color: "#666" }}>
            Phone: {record.applicantDetails.phone}
          </div>
          <div style={{ fontSize: 11, color: "#666" }}>
            Applied: {record.application_date.format("MMM D, YYYY")}
          </div>
        </div>
      ),
    },
    {
      title: "Product",
      dataIndex: "product",
    },
    {
      title: "Amount (ZMW)",
      dataIndex: "amount",
      render: (value) => value.toLocaleString(),
    },
    {
      title: "Purpose",
      dataIndex: "loan_purpose",
      render: (purpose) => <Tag color="blue">{formatLoanPurpose(purpose)}</Tag>,
    },
    {
      title: "Current Stage",
      dataIndex: "current_stage",
      render: (stage) => <Tag color={stage.color}>{stage.label}</Tag>,
    },
    {
      title: "Processing Time",
      dataIndex: "processing_time",
      render: (days) => `${days} days`,
    },
    {
      title: "History",
      dataIndex: "status_history",
      render: (history) => (
        <Timeline mode="left">
          {history.map((entry, index) => (
            <Timeline.Item
              key={index}
              color={applicationStages[entry.status]?.color || "gray"}
            >
              {moment(entry.timestamp).format("MMM D")}:{" "}
              {applicationStages[entry.status]?.label || "Unknown"}
            </Timeline.Item>
          ))}
        </Timeline>
      ),
    },
  ];

  // Export data to CSV function
  const exportToCSV = () => {
    if (applications.length === 0) {
      message.info("No data to export");
      return;
    }

    // Create CSV header row
    const headers = [
      "ID",
      "Applicant",
      "NRC",
      "Phone",
      "Address",
      "Employer",
      "Product",
      "Amount",
      "Purpose",
      "Application Date",
      "Current Stage",
      "Processing Time",
      "Assigned Officer",
    ];

    // Create CSV data rows
    const csvData = applications.map((app) => [
      app.id,
      app.applicant,
      app.applicantDetails.nrc,
      app.applicantDetails.phone,
      app.applicantDetails.address,
      app.applicantDetails.employer,
      app.product,
      app.amount,
      formatLoanPurpose(app.loan_purpose),
      app.application_date.format("YYYY-MM-DD"),
      app.current_stage.label,
      `${app.processing_time} days`,
      app.assigned_officer,
    ]);

    // Combine header and rows
    const csvContent = [headers, ...csvData]
      .map((row) => row.join(","))
      .join("\n");

    // Create download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `application_pipeline_${moment().format("YYYY-MM-DD")}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success("Export completed successfully");
  };

  // Function to handle tour guide
  const handleStartTour = () => {
    setTourHighlight(tourGuide[0].highlight);
    setShowTourModal(true);
  };

  const handleNextStep = () => {
    if (currentTourStep < tourGuide.length - 1) {
      const nextStep = currentTourStep + 1;
      setCurrentTourStep(nextStep);
      setTourHighlight(tourGuide[nextStep].highlight);
    } else {
      // If reached the end, close tour
      setShowTourModal(false);
      setTourHighlight(null);
      setCurrentTourStep(0);
      // Remember that user has seen the tour
      localStorage.setItem("hasSeenApplicationPipelineTour", "true");
    }
  };

  const handleCloseTour = () => {
    setShowTourModal(false);
    setTourHighlight(null);
    setCurrentTourStep(0);
    localStorage.setItem("hasSeenApplicationPipelineTour", "true");
  };

  // Check if this is the first visit to show the tour
  useEffect(() => {
    const hasSeenTour = localStorage.getItem("hasSeenApplicationPipelineTour");
    if (!hasSeenTour) {
      handleStartTour();
    }
  }, []);

  return (
    <div style={{ padding: 24 }}>
      {/* Inject tour styles */}
      <style>{tourStyles}</style>

      {/* Tour Guide Modal */}
      <Modal
        title={
          tourGuide[currentTourStep]?.title || "Application Pipeline Guide"
        }
        open={showTourModal}
        onCancel={handleCloseTour}
        footer={[
          <Button key="close" onClick={handleCloseTour}>
            Close
          </Button>,
          <Button key="next" type="primary" onClick={handleNextStep}>
            {currentTourStep < tourGuide.length - 1 ? "Next" : "Finish"}
          </Button>,
        ]}
      >
        <p>{tourGuide[currentTourStep]?.content}</p>
      </Modal>

      <Card bordered={false}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
          className={tourHighlight === "title-section" ? "tour-highlight" : ""}
          id="title-section"
        >
          <Col>
            <h2>Application Pipeline Report</h2>
            <p>Track and analyze loan application flow and status changes</p>
          </Col>
          <Col>
            <Space>
              <Tooltip title="View Report Guide">
                <Button
                  icon={<QuestionCircleOutlined />}
                  onClick={handleStartTour}
                >
                  Guide
                </Button>
              </Tooltip>
              <Button
                className={
                  tourHighlight === "export-button" ? "tour-highlight" : ""
                }
                id="export-button"
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={exportToCSV}
                disabled={loading || applications.length === 0}
              >
                Export Report
              </Button>
            </Space>
          </Col>
        </Row>

        <Row
          gutter={16}
          style={{ marginBottom: 24 }}
          className={tourHighlight === "date-filter" ? "tour-highlight" : ""}
          id="date-filter"
        >
          <Col span={8}>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
              style={{ width: "100%" }}
            />
          </Col>
          <Col
            span={8}
            className={
              tourHighlight === "status-filter" ? "tour-highlight" : ""
            }
            id="status-filter"
          >
            <Select
              style={{ width: "100%" }}
              placeholder="Filter by Status"
              onChange={(value) => setFilters({ ...filters, status: value })}
            >
              <Select.Option value="all">All Statuses</Select.Option>
              {Object.entries(applicationStages).map(([key, stage]) => (
                <Select.Option key={key} value={key}>
                  {stage.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col
            span={8}
            className={
              tourHighlight === "product-filter" ? "tour-highlight" : ""
            }
            id="product-filter"
          >
            <Select
              style={{ width: "100%" }}
              placeholder="Filter by Product"
              onChange={(value) => setFilters({ ...filters, product: value })}
              loading={loading}
            >
              <Select.Option value="all">All Products</Select.Option>
              {loanProducts.map((product) => (
                <Select.Option key={product.id} value={product.id}>
                  {product.loan_name}
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row
          gutter={16}
          style={{ marginBottom: 24 }}
          className={tourHighlight === "metrics-row" ? "tour-highlight" : ""}
          id="metrics-row"
        >
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Applications"
                value={metrics.total}
                prefix={<FileDoneOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Conversion Rate"
                value={metrics.conversionRate}
                suffix="%"
                prefix={<LineChartOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Avg Processing Time"
                value={metrics.avgProcessingTime}
                suffix="days"
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Pending Review"
                value={metrics.byStage["Under Review"] || 0}
                prefix={<AuditOutlined />}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Card
              title="Application Stage Distribution"
              className={
                tourHighlight === "stage-chart" ? "tour-highlight" : ""
              }
              id="stage-chart"
            >
              <Bar
                data={chartData}
                options={{
                  responsive: true,
                  scales: {
                    y: { beginAtZero: true },
                  },
                }}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card
              title="Stage Progression"
              className={
                tourHighlight === "stage-timeline" ? "tour-highlight" : ""
              }
              id="stage-timeline"
            >
              <Progress
                percent={metrics.conversionRate}
                status="active"
                format={(percent) => `${percent}% Conversion`}
              />
              <div style={{ marginTop: 16 }}>
                <Timeline mode="alternate">
                  {Object.values(applicationStages).map((stage) => (
                    <Timeline.Item
                      key={stage.label}
                      color={stage.color}
                      dot={
                        <Tag color={stage.color}>
                          {metrics.byStage[stage.label] || 0}
                        </Tag>
                      }
                    >
                      {stage.label}
                    </Timeline.Item>
                  ))}
                </Timeline>
              </div>
            </Card>
          </Col>
        </Row>

        <Card
          title="Detailed Applications"
          bordered={false}
          className={
            tourHighlight === "applications-table" ? "tour-highlight" : ""
          }
          id="applications-table"
        >
          {loading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin size="large" />
              <div style={{ marginTop: 20 }}>Loading application data...</div>
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={applications}
              rowKey="id"
              loading={loading}
              scroll={{ x: true }}
              pagination={{ pageSize: 10 }}
            />
          )}
        </Card>
      </Card>
    </div>
  );
};

export default ApplicationPipelineReport;
